import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StatusService } from '../projectStatus/status.service';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  constructor(private statusService: StatusService) {}

  private filtersSubject = new BehaviorSubject<
    Partial<{
      name?: string;
      status?: string;
      priceRange?: [number, number | string];
      dateRange: Date | null;
    }>
  >({});
  filters$ = this.filtersSubject.asObservable();

  private dataSubject = new BehaviorSubject<any[]>([]);
  data$ = this.dataSubject.asObservable();

  private filteredDataSubject = new BehaviorSubject<any[]>([]);
  filteredData$ = this.filteredDataSubject.asObservable();

  /**
   * Carga los datos iniciales en el servicio.
   */
  loadData(data: any[]): void {
    this.dataSubject.next(data);
  }

  /**
   * Actualiza los filtros y reaplica los filtros a los datos.
   */
  updateFilters(
    newFilters: Partial<{
      name?: string;
      status?: string;
      dateRange: Date | null;
      startDate: Date | null;
      endDate: Date | null;
      orderBy?: string;
      location?: string;
    }>
  ): void {
    const updatedFilters: any = {
      ...newFilters,
    };
    this.applyFilters(updatedFilters);
  }

  /**
   * Resetea los filtros a su estado inicial.
   */
  resetFilters(): void {
    this.filtersSubject.next({});
    this.filteredDataSubject.next(this.dataSubject.getValue());
  }

  /**
   * Aplica los filtros actuales a los datos y los emite en `filteredData$`.
   */
  private applyFilters(filters: {
    name?: string | undefined;
    status?: string | undefined;
    dateRange: Date | null;
    startDate: Date | null;
    endDate: Date | null;
    orderBy?: string | undefined;
    location?: string | undefined;
  }): void {
    let filteredData = [...this.dataSubject.getValue()];

    // Filtro por nombre
    if (filters.name?.trim()) {
      const nameFilter = filters.name.trim().toLowerCase();
      filteredData = filteredData.filter((item) =>
        item.nameTokens?.toLowerCase().includes(nameFilter)
      );
    }

    // Filtro por estado
    if (filters.status?.length) {
      filteredData = filteredData.filter((item) => {
        // Calcula eurosSold una sola vez
        const eurosSold = item.amountSold * (item.priceToken / 100);
        const currentStatus = this.statusService.getCurrentStatus(item, eurosSold);
        
        // Comprueba específicamente para el caso de "Vendido"
        if (filters.status === 'Vendido' && item.proyectStatus === 'Vendido') {
          return true;
        }
        
        // Para otros estados, usa la lógica normal
        return Array.isArray(currentStatus) && currentStatus.includes(filters.status!);
      });
    }

    // Filtro por rango de fechas
    if (filters.startDate && filters.endDate) {
      filteredData = this.filterDateByRange(
        filteredData,
        filters.startDate,
        filters.endDate
      );
    }

    // Filtro por ubicación
    if (filters.location && filters.location !== 'all') {
      const normalize = (s: string) =>
        s.toLowerCase().trim().normalize('NFD').replace(/\p{Diacritic}/gu, '');
      const locationFilter = normalize(filters.location);
      filteredData = filteredData.filter((item) =>
        typeof item.location === 'string' &&
        normalize(item.location).includes(locationFilter)
      );
    }

    // Ordena los datos filtrados
    if (filters.orderBy) {
      filteredData = filteredData.sort((a: any, b: any) => {
        const amountA = a.annualReturn;
        const amountB = b.annualReturn;

        if (filters.orderBy === 'order_asc') {
          return amountB - amountA;
        } else if (filters.orderBy === 'order_desc') {
          return amountA - amountB;
        }
        return 0;
      });
    }

    // Emite los datos filtrados
    this.filteredDataSubject.next(filteredData);
  }

  /**
   * Filtra los datos por rango de fechas.
   */
  private filterDateByRange(
    filteredData: any[],
    startDate: Date,
    endDate: Date
  ): any[] {
    if (!startDate || !endDate || !Array.isArray(filteredData) || filteredData.length === 0) {
      return filteredData;
    }

    const startTimestamp = new Date(startDate).getTime();
    const endTimestamp = new Date(endDate).getTime();

    return filteredData.filter((item) => {
      if (!item.activationDate || typeof item.activationDate.seconds !== 'number') {
        return false;
      }

      const itemTimestamp = item.activationDate.seconds * 1000;
      return itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp;
    });
  }
}